/**
 * CetusEd Admin Console — React Query Hooks
 *
 * COMPLIANCE: All admin API interactions are typed and auditable. Query keys
 *   follow the pattern: ['admin', 'domain', identifier]. Mutations invalidate
 *   relevant query caches and trigger audit log entries server-side.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from './client';

// ---------------------------------------------------------------------------
// Districts
// ---------------------------------------------------------------------------

export interface DistrictSummary {
  id: string;
  name: string;
  state: string;
  status: 'active' | 'suspended' | 'provisioning' | 'deactivated';
  userCount: number;
  studentCount: number;
  storageUsedBytes: number;
  deploymentMode: string;
  createdAt: string;
  updatedAt: string;
}

export function useDistricts() {
  return useQuery({
    queryKey: ['admin', 'districts'],
    queryFn: ({ signal }) => adminApi.get<DistrictSummary[]>('/districts', signal),
  });
}

export function useDistrict(districtId: string) {
  return useQuery({
    queryKey: ['admin', 'districts', districtId],
    queryFn: ({ signal }) => adminApi.get<DistrictSummary>(`/districts/${districtId}`, signal),
    enabled: !!districtId,
  });
}

export function useCreateDistrict() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { name: string; state: string; deploymentMode: string }) =>
      adminApi.post<DistrictSummary>('/districts', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'districts'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export interface AdminAuditEntry {
  id: string;
  districtId?: string;
  actorId: string;
  actorEmail: string;
  action: string;
  resource?: string;
  details?: Record<string, unknown>;
  timestamp: string;
  ipAddress?: string;
}

export function useAdminAuditLog(params?: { districtId?: string; action?: string; limit?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.districtId) searchParams.set('districtId', params.districtId);
  if (params?.action) searchParams.set('action', params.action);
  if (params?.limit) searchParams.set('limit', String(params.limit));

  return useQuery({
    queryKey: ['admin', 'audit', params],
    queryFn: ({ signal }) => adminApi.get<AdminAuditEntry[]>(`/audit?${searchParams.toString()}`, signal),
  });
}

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

export interface BillingSummary {
  districtId: string;
  districtName: string;
  plan: string;
  status: 'active' | 'past_due' | 'canceled' | 'trialing';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  monthlyCost: number;
  storageCost: number;
  totalCost: number;
}

export function useBillingSummary() {
  return useQuery({
    queryKey: ['admin', 'billing'],
    queryFn: ({ signal }) => adminApi.get<BillingSummary[]>('/billing/summary', signal),
  });
}

export interface Invoice {
  id: string;
  districtId: string;
  districtName: string;
  invoiceNumber: string;
  amount: number;
  status: 'paid' | 'open' | 'void' | 'uncollectible';
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  paidAt?: string;
  pdfUrl?: string;
}

export function useInvoices(districtId?: string) {
  const params = districtId ? `?districtId=${districtId}` : '';
  return useQuery({
    queryKey: ['admin', 'billing', 'invoices', districtId],
    queryFn: ({ signal }) => adminApi.get<Invoice[]>(`/billing/invoices${params}`, signal),
  });
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  storageIncluded: number;
  userLimit: number;
  features: string[];
}

export function usePlans() {
  return useQuery({
    queryKey: ['admin', 'billing', 'plans'],
    queryFn: ({ signal }) => adminApi.get<Plan[]>('/billing/plans', signal),
  });
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { districtId: string; planId: string }) =>
      adminApi.patch<void>(`/billing/districts/${params.districtId}/plan`, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'billing'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Monitoring
// ---------------------------------------------------------------------------

export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'down';
  uptime: number;
  apiLatencyMs: number;
  dbLatencyMs: number;
  storageUsed: number;
  activeDistricts: number;
  totalUsers: number;
  errorRate24h: number;
  lastIncident?: string;
}

export function useSystemHealth() {
  return useQuery({
    queryKey: ['admin', 'monitoring', 'health'],
    queryFn: ({ signal }) => adminApi.get<SystemHealth>('/monitoring/health', signal),
    refetchInterval: 30_000,
  });
}

export interface Alert {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  districtId?: string;
  acknowledged: boolean;
  acknowledgedBy?: string;
  createdAt: string;
  resolvedAt?: string;
}

export function useAlerts() {
  return useQuery({
    queryKey: ['admin', 'monitoring', 'alerts'],
    queryFn: ({ signal }) => adminApi.get<Alert[]>('/monitoring/alerts', signal),
    refetchInterval: 60_000,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => adminApi.post<void>(`/monitoring/alerts/${alertId}/acknowledge`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'monitoring', 'alerts'] });
    },
  });
}

export interface RateLimitStatus {
  districtId: string;
  districtName: string;
  currentRate: number;
  limit: number;
  remaining: number;
  resetAt: string;
  violations24h: number;
}

export function useRateLimits() {
  return useQuery({
    queryKey: ['admin', 'monitoring', 'rate-limits'],
    queryFn: ({ signal }) => adminApi.get<RateLimitStatus[]>('/monitoring/rate-limits', signal),
  });
}

// ---------------------------------------------------------------------------
// Integrations
// ---------------------------------------------------------------------------

export interface SSOProviderConfig {
  districtId: string;
  provider: string;
  enabled: boolean;
  issuer?: string;
  clientId?: string;
  lastVerified?: string;
}

export function useSSOConfig(districtId: string) {
  return useQuery({
    queryKey: ['admin', 'integrations', 'sso', districtId],
    queryFn: ({ signal }) => adminApi.get<SSOProviderConfig>(`/districts/${districtId}/sso`, signal),
    enabled: !!districtId,
  });
}

export function useUpdateSSOConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { districtId: string; config: Partial<SSOProviderConfig> }) =>
      adminApi.put<void>(`/districts/${params.districtId}/sso`, params.config),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'integrations', 'sso', variables.districtId] });
    },
  });
}

export interface SISSyncConfig {
  districtId: string;
  provider: string;
  enabled: boolean;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  syncIntervalMinutes: number;
}

export function useSISConfig(districtId: string) {
  return useQuery({
    queryKey: ['admin', 'integrations', 'sis', districtId],
    queryFn: ({ signal }) => adminApi.get<SISSyncConfig>(`/districts/${districtId}/sis`, signal),
    enabled: !!districtId,
  });
}

export function useUpdateSISConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { districtId: string; config: Partial<SISSyncConfig> }) =>
      adminApi.put<void>(`/districts/${params.districtId}/sis`, params.config),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'integrations', 'sis', variables.districtId] });
    },
  });
}

export interface WebhookConfigData {
  districtId: string;
  endpoints: Array<{
    id: string;
    url: string;
    events: string[];
    enabled: boolean;
    secret: string;
  }>;
}

export function useWebhookConfig(districtId: string) {
  return useQuery({
    queryKey: ['admin', 'integrations', 'webhooks', districtId],
    queryFn: ({ signal }) => adminApi.get<WebhookConfigData>(`/districts/${districtId}/webhooks`, signal),
    enabled: !!districtId,
  });
}

export interface EmailProviderConfig {
  districtId: string;
  provider: string;
  enabled: boolean;
  fromAddress: string;
  verified: boolean;
}

export function useEmailConfig(districtId: string) {
  return useQuery({
    queryKey: ['admin', 'integrations', 'email', districtId],
    queryFn: ({ signal }) => adminApi.get<EmailProviderConfig>(`/districts/${districtId}/email`, signal),
    enabled: !!districtId,
  });
}

// ---------------------------------------------------------------------------
// Support
// ---------------------------------------------------------------------------

export interface SupportTicket {
  id: string;
  districtId: string;
  districtName: string;
  subject: string;
  status: 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'critical';
  createdBy: string;
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
}

export function useSupportTickets(status?: string) {
  const params = status ? `?status=${status}` : '';
  return useQuery({
    queryKey: ['admin', 'support', 'tickets', status],
    queryFn: ({ signal }) => adminApi.get<SupportTicket[]>(`/support/tickets${params}`, signal),
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { districtId: string; subject: string; body: string; priority: string }) =>
      adminApi.post<void>('/support/tickets', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'tickets'] });
    },
  });
}

export interface Announcement {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'maintenance';
  published: boolean;
  publishedAt?: string;
  createdBy: string;
  createdAt: string;
  targetAudience: 'all' | 'admins' | 'district';
}

export function useAnnouncements() {
  return useQuery({
    queryKey: ['admin', 'support', 'announcements'],
    queryFn: ({ signal }) => adminApi.get<Announcement[]>('/support/announcements', signal),
  });
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { title: string; body: string; severity: string; targetAudience: string }) =>
      adminApi.post<void>('/support/announcements', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'announcements'] });
    },
  });
}

export function useImpersonation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { districtId: string; userId: string; reason: string }) =>
      adminApi.post<{ impersonationToken: string; expiresIn: number }>('/support/impersonate', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}
